/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import {
  MenuGroupPropagateStatus,
  menuNameTooLongBEMessage,
  menuNameAlreadyExistBEMessage,
} from "constants/menu_group"
import { AnyAction, Dispatch } from "redux"
import { initialMenuGroup } from "store/duck/states/menuState"
import { actionCreator } from "store/store"
import { AxiosAction } from "types/api"
import { MenuGroup } from "types/models/menu"
import { sleep, getDalErrMessage } from "utils/misc"
import notification from "components/common/notification"
import { HTTPStatus } from "constants/endpoints"

export const waitMenuGroupPropagete = async ({
  dispatch,
  menuGroupID,
  merchantGroupID,
}: {
  dispatch: Dispatch
  menuGroupID: string
  merchantGroupID: string
}) => {
  let propagatingMenuGroup = initialMenuGroup()
  do {
    // eslint-disable-next-line no-await-in-loop
    const respMenuGroupDetail = (await dispatch(
      actionCreator.menu.fetchMenuGroupDetail({
        menuGroupID,
        merchantGroupID,
      }) as AnyAction,
    )) as AxiosAction<{ menuGroup: MenuGroup }>

    propagatingMenuGroup = respMenuGroupDetail?.payload?.data?.menuGroup

    // eslint-disable-next-line no-await-in-loop
    await sleep(1000)
  } while (
    propagatingMenuGroup?.latestPropagateStatus !==
    MenuGroupPropagateStatus.NonPropagating
  )
}

export const getIsMenuGroupMartDepartmentStructure = (menuGroup: MenuGroup) =>
  menuGroup.menuType === 1

export interface ApplyUpdateMenuGroupClientErrorOptions {
  errCode?: number
  errMessage?: string
  menuGroupName: string
  menuGroupNameField: string
  maxLengthMenuGroupName: number
  globalErrorMessage: string
  textMap: Record<string, string>
  setFields: (fields: Record<string, unknown>) => void
}

/**
 * Shared client-side error handling for updateMenuGroup.
 * Legacy Redux has no errorCallback — the drawer maps BE errors to form fields or a toast.
 */
export const applyUpdateMenuGroupClientError = ({
  errCode,
  errMessage = "",
  menuGroupName,
  menuGroupNameField,
  maxLengthMenuGroupName,
  globalErrorMessage,
  textMap,
  setFields,
}: ApplyUpdateMenuGroupClientErrorOptions): void => {
  if (
    errCode === HTTPStatus.BadRequest &&
    getDalErrMessage(errMessage) === menuNameTooLongBEMessage
  ) {
    setFields({
      [menuGroupNameField]: {
        value: menuGroupName,
        errors: [
          new Error(
            textMap.menuNameCharExceeded.replace(
              "<x>",
              `${maxLengthMenuGroupName}`,
            ),
          ),
        ],
      },
    })
    return
  }

  if (
    errCode === HTTPStatus.BadRequest &&
    getDalErrMessage(errMessage) === menuNameAlreadyExistBEMessage
  ) {
    setFields({
      [menuGroupNameField]: {
        value: menuGroupName,
        errors: [new Error(textMap.menuNameAlreadyExist)],
      },
    })
    return
  }

  if (errCode) {
    notification.error({
      message: globalErrorMessage,
    })
  }
}
